import { FC, useCallback, useMemo } from 'react';
import {
  ActionButton,
  ClassifierFormDataType,
  ClassifierSchema,
  ClassifierValidFormDataType,
  confirmationHeaders,
  DetailLayout,
  DropdownItem,
  EditLock,
  Form,
  FormAlerts,
  GetApiErrorMessage,
  InfoIconUrl,
  ObjectType,
  Padding,
  PopconfirmDelete,
  Spinner,
  useIsClassifierUploading,
  useMutateClassifierQuery,
  useMutationErrors,
  useNavigationBlockers,
  useAlertContext,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { path } from '../../../paths';
import { useNavigateTo } from '../../../paths/useNavigateTo';
import translations from './ClassifierForm.translations.json';
import { ClassifierFormLeftSideFields } from './ClassifierFormLeftSideFields';
import { ClassifierFromLeftSideHeader } from './ClassifierFromLeftSideHeader';
import { ClassifierFormUploadDownloadActions } from './ClassifierFormUploadDownloadActions';
import { ClassifierFormSegments } from './ClassifierFormSegments';
import { ClassifierFormBreadcrumbs } from './ClassifierFormBreadcrumbs';
import { ClassifierFormTotalEntries } from './ClassifierFormTotalEntries';
import { useClassifierFormLabelTranslation } from './useClassifierFormLabelTranslation';
import styled from 'styled-components';

const { addTranslations, useTranslation, t } = i18nNsFactory('classifier-form');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export type ClassifierFormProps =
  | {
      mode: 'create';
      id?: never;
      defaultValues: ClassifierFormDataType | ClassifierValidFormDataType;
      onClickCancel: () => void;
    }
  | {
      mode: 'edit';
      id: number;
      defaultValues: ClassifierFormDataType | ClassifierValidFormDataType;
      onClickCancel: () => void;
    }
  | {
      mode: 'clone';
      id?: never;
      defaultValues: ClassifierFormDataType | ClassifierValidFormDataType;
      onClickCancel: () => void;
    };

export const ClassifierForm: FC<ClassifierFormProps> = (props) => {
  const { mode, id, defaultValues, onClickCancel } = props;
  const { clear: clearBlockers } = useNavigationBlockers();
  const { t } = useTranslation();
  const { t: tLabel } = useClassifierFormLabelTranslation();
  const { mutateAsync } = useMutateClassifierQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const navigateToIndex = useNavigateTo(path.classifierDirectory);
  const navigateToClassifierEdit = useNavigateTo(path.classifierEdit);
  const navigateToClassifierClone = useNavigateTo(path.classifierClone);
  const { resetAlert, setSuccess } = useAlertContext();
  const isUploading = useIsClassifierUploading();

  const handleDelete = useCallback(() => {
    resetAlert();
    if (mode === 'edit') {
      clearMutationErrors();
      return mutateAsync({ id })
        .then(() => {
          navigateToIndex(undefined, { state: { noBlocker: true } });
          setSuccess(t('alert.success.deleted'));
        })
        .catch(({ response }) => {
          setMutationErrors(response?.data.errors ?? []);
        });
    }
  }, [
    resetAlert,
    mode,
    clearMutationErrors,
    mutateAsync,
    id,
    navigateToIndex,
    setSuccess,
    t,
    setMutationErrors,
  ]);

  const handleFinish = useCallback(
    (values: ClassifierValidFormDataType, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: mode === 'edit' ? id : undefined,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then((responseData) => {
          if (responseData) {
            if (Object.hasOwn(responseData, 'id')) {
              const isCreateOrClone = mode === 'create' || mode === 'clone';

              if (isCreateOrClone) {
                navigateToClassifierEdit(
                  { id: responseData.id },
                  {
                    state: { noBlocker: true },
                  },
                );
              }

              setSuccess(
                isCreateOrClone ? t('alert.success.created') : undefined,
              );
              clearBlockers();
            } else {
              throw new Error('Expecting "id" in response data.');
            }
          }
        })
        .catch((error) => {
          setMutationErrors(error.response?.data.errors ?? []);
          throw error;
        });
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      mode,
      id,
      setSuccess,
      t,
      clearBlockers,
      navigateToClassifierEdit,
      setMutationErrors,
    ],
  );

  const dropdownItems = useMemo(
    () => [
      {
        key: 'clone',
        label: (
          <DropdownItem
            onClick={() => navigateToClassifierClone({ id: id as number })}
          >
            {t('button.clone.label')}
          </DropdownItem>
        ),
      },
      {
        key: 'delete',
        label: (
          <PopconfirmDelete
            stopClickPropagation
            onDelete={handleDelete}
            genitiveModelName={t('delete.genitive')}
          />
        ),
      },
    ],
    [t, handleDelete, navigateToClassifierClone, id],
  );

  return (
    <Form
      schema={ClassifierSchema.validFormDataSchema}
      defaultValues={defaultValues}
      serverErrors={mutationErrors}
      onSubmit={handleFinish}
    >
      <DetailLayout
        infoIconUrl={InfoIconUrl.CLASSIFIER_DETAIL}
        breadCrumb={<ClassifierFormBreadcrumbs />}
        side={{
          header: <ClassifierFromLeftSideHeader />,
          content: <ClassifierFormLeftSideFields mode={mode} />,
          footer:
            mode === 'edit' &&
            'id' in defaultValues &&
            'modifiedAt' in defaultValues &&
            'modifiedBy' in defaultValues ? (
              <EditLock
                id={defaultValues.id ?? undefined}
                entity={defaultValues}
                objectType={ObjectType.Classifier}
                onTimeout={onClickCancel}
              />
            ) : undefined,
        }}
        headerConfig={{
          actions: (
            <>
              <ClassifierFormUploadDownloadActions
                id={id}
                onError={setMutationErrors}
              />
              <ActionButton type="abort" onClick={onClickCancel} />
              <ActionButton type="formSubmit" />
            </>
          ),
          dropdownMenu: mode === 'edit' ? { items: dropdownItems } : undefined,
        }}
        footerContent={<ClassifierFormTotalEntries />}
        alert={<FormAlerts tLabel={tLabel} />}
      >
        <ContentWrapper>
          <Spinner overlay delay={1000} isLoading={isUploading > 0}>
            <ClassifierFormSegments />
          </Spinner>
        </ContentWrapper>
      </DetailLayout>
    </Form>
  );
};

const ContentWrapper = styled(Padding)`
  width: 100%;
  min-width: fit-content;
  min-height: fit-content;
`;
